// frontend/src/context/BrandContext.js
// Marca activa (Tecno/Freeo/Genérica). Se persiste en localStorage para que
// la elección del login se mantenga durante toda la sesión.

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { BRANDS, DEFAULT_BRAND } from '../theme';

const STORAGE_KEY = 'optimus.brand';

const BrandContext = createContext(null);

export function BrandProvider({ children }) {
  const [brandKey, setBrandKeyState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && BRANDS[saved]) return saved;
    } catch (e) { /* localStorage no disponible */ }
    return DEFAULT_BRAND;
  });

  const setBrandKey = useCallback((key) => {
    if (!BRANDS[key]) return;
    setBrandKeyState(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch (e) { /* ignore */ }
  }, []);

  // Aplica variables CSS al :root para que App.css pueda usarlas
  useEffect(() => {
    const brand = BRANDS[brandKey];
    const root = document.documentElement;
    root.style.setProperty('--brand-primary',        brand.primary);
    root.style.setProperty('--brand-primary-deep',   brand.primaryDeep);
    root.style.setProperty('--brand-primary-hover',  brand.primaryHover);
    root.style.setProperty('--brand-primary-active', brand.primaryActive);
    root.style.setProperty('--brand-primary-soft',   brand.primarySoft);
    root.style.setProperty('--brand-accent',         brand.accent);
    root.style.setProperty('--brand-accent-soft',    brand.accentSoft);
    root.dataset.brand = brandKey;

    // Título de la pestaña + favicon dinámicos por marca
    document.title = brandKey === 'generic' ? 'Optimus' : `Optimus ${brand.name}`;
    const favicon = document.getElementById('app-favicon');
    if (favicon) {
      const publicUrl = process.env.PUBLIC_URL || '';
      const faviconByBrand = {
        tecno:   `${publicUrl}/logo-tecno.png`,
        freeo:   `${publicUrl}/logo-freeo.png`,
        generic: `${publicUrl}/favicon.ico`,
      };
      favicon.setAttribute('href', faviconByBrand[brandKey] || faviconByBrand.generic);
    }
  }, [brandKey]);

  const value = useMemo(() => ({
    brandKey,
    brand: BRANDS[brandKey],
    brands: BRANDS,
    setBrandKey,
  }), [brandKey, setBrandKey]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand debe usarse dentro de <BrandProvider>');
  return ctx;
}
